import * as NodeFS from "fs";
import * as FS from "fs-jetpack";
import * as fs from "fs-jetpack";
import * as Path from "path";
import * as path from "path";
import * as OS from "os";

export function join(...segments: string[]) {
  return path.join(...segments);
}

/**
 * Strip the extension of a file path.
 *
 * This can be handy for example when going from a file to a module path
 * suitable for import like a user would do, not supplying the ext.
 */
export function stripExt(filePath: string): string {
  const { dir, name } = path.parse(filePath);
  return path.join(dir, name);
}

export const baseIgnores = ["!node_modules/**/*", "!.*/**/*"];

export function trimNodeModulesIfInPath(path: string) {
  if (path.includes("node_modules")) {
    return path.substring(
      path.indexOf("node_modules") + "node_modules".length + 1
    );
  }

  return path;
}

export function findFile(
  fileNames: string | string[],
  config: { ignore?: string[]; projectRoot: string }
): null | string {
  const paths = Array.isArray(fileNames) ? fileNames : [fileNames];
  const projectRoot = config.projectRoot;
  const localFs = fs.cwd(projectRoot);

  const foundFiles = localFs.find({
    matching: [
      ...paths,
      "!node_modules/**/*",
      "!.yalc/**/*",
      ...(config?.ignore?.map((i) => `!${i}`) ?? []),
    ],
  });

  // TODO: What if several files were found?
  if (foundFiles.length > 0) {
    return path.join(projectRoot, foundFiles[0]);
  }

  return null;
}

export async function findFiles(
  fileNames: string | string[],
  config?: { ignore?: string[]; cwd?: string }
): Promise<string[]> {
  const cwd = config?.cwd ?? process.cwd();
  const paths = Array.isArray(fileNames) ? fileNames : [fileNames];
  const localFS = fs.cwd(cwd);

  const files = await localFS.findAsync({
    matching: [
      ...paths,
      ...baseIgnores,
      ...(config?.ignore?.map((i) => `!${i}`) ?? []),
    ],
  });

  return files.map((f) => (path.isAbsolute(f) ? f : localFS.path(f)));
}

/**
 * Search for a file in cwd or in parent directory recursively up to the root
 * directory.
 */
export async function findFileRecurisvelyUpward(
  fileName: string,
  opts: { projectRoot: string }
): Promise<null | string> {
  let found: null | string = null;
  let currentDir = opts.projectRoot;

  while (true) {
    const checkFilePath = Path.join(currentDir, fileName);

    if (await FS.existsAsync(checkFilePath)) {
      found = checkFilePath;
      break;
    }

    if (currentDir === "/") {
      break;
    }

    currentDir = Path.dirname(currentDir);
  }

  return found;
}

/**
 * Search for a file in cwd or in parent directory recursively up to the root
 * directory.
 */
export function findFileRecurisvelyUpwardSync(
  fileName: string,
  opts: { cwd: string }
): { path: string; dir: string } | null {
  let found: { path: string; dir: string } | null = null;
  let currentDir = opts.cwd;
  const localFS = FS.cwd(currentDir);

  while (true) {
    const filePath = Path.join(currentDir, fileName);

    if (localFS.exists(filePath)) {
      found = { dir: currentDir, path: filePath };
      break;
    }

    if (currentDir === "/") {
      break;
    }

    currentDir = Path.join(currentDir, "..");
  }

  return found;
}

/**
 * Return the path to a temporary directory on the machine. This works around a
 * limitation in Node wherein a symlink is returned on macOS for `os.tmpdir`.
 */
export function getTmpDir(prefix: string = "") {
  const tmpDirPath = NodeFS.realpathSync(OS.tmpdir());
  const id = Math.random().toString().slice(2);
  const dirName = [prefix, id].filter((x) => x).join("-");

  // https://github.com/nodejs/node/issues/11422
  const tmpDir = Path.join(tmpDirPath, dirName);

  return tmpDir;
}

export function getTranspiledPath(
  projectDir: string,
  filePath: string,
  outDir: string
) {
  const pathFromRootToFile = path.relative(projectDir, filePath);
  const jsFileName = path.basename(pathFromRootToFile, ".ts") + ".js";
  const pathToJsFile = path.join(path.dirname(pathFromRootToFile), jsFileName);

  return path.join(outDir, pathToJsFile);
}

export function sourceFilePathFromTranspiledPath({
  transpiledPath,
  outDir,
  rootDir,
  packageJsonPath,
}: {
  transpiledPath: string;
  outDir: string;
  rootDir: string;
  packageJsonPath: string;
}) {
  const normalizedTranspiledPath = transpiledPath.startsWith("/")
    ? transpiledPath
    : path.join(packageJsonPath, transpiledPath);

  const pathFromOutDirToFile = path.relative(outDir, normalizedTranspiledPath);
  const tsFileName = path.basename(pathFromOutDirToFile, ".js") + ".ts";
  const maybeAppFolders = path.dirname(pathFromOutDirToFile);

  return path.join(rootDir, maybeAppFolders, tsFileName);
}
